<script lang="ts">
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {LogbookReportPairingType, PairUnpairEventData} from './types';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {exists} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {tooltip} from 'shared/ui-widgets/tooltip/use.tooltip';
    import {getIdFromGridFieldValue, getTextFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
    import {createEventDispatcher} from 'svelte';
    import {getReportChargedValue, getReportReportedValue} from 'src/features/erp-sutor/features/report-detail';
    import {FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
    import {SUTOR_REPORT_STATES} from 'src/features/erp-sutor/sutor-constants';
    import GridFieldValue from 'shared/ui-widgets/grid/GridFieldValue.svelte';
    import KpUserAvatar from 'shared/components/kp-user-avatar/KpUserAvatar.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpChipTag from 'shared/ui-widgets/chip/KpChipTag.svelte';
    import RecordLinkEyeButton from 'shared/ui-widgets/table/RecordLinkEyeButton.svelte';

    export let record: RecordRow;
    export let pairingType: LogbookReportPairingType;
    export let isProcessing: boolean;

    const dispatch = createEventDispatcher<{'pair-unpair': PairUnpairEventData}>();

    const dateField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.dateFieldId);
    const workerField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.workerFieldId);
    const machineField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.machineFieldId);
    const nameField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.nameFieldId);
    const stateField = getFirstFieldByFieldTypeIdRecursive(record, FOND_REPORT.stateFieldId);

    $: reportedValueStrings = getReportReportedValue(record, false);
    $: chargedValueStrings = getReportChargedValue(record);
    $: canPair = getIdFromGridFieldValue(stateField) === SUTOR_REPORT_STATES.CONFIRMED;

    const handlePairUnpairClick = () => {
        dispatch('pair-unpair', {record, pairingType});
    };
</script>

<td>
    <RecordLinkEyeButton recordId={record.id}/>
</td>

{#if pairingType === 'pair'}
    <td>
        <KpButton isLoading={isProcessing}
                  isDisabled={!canPair}
                  tooltipLabel={canPair ? null : 'Výkaz musí být ve stavu Schváleno'}
                  noWrap
                  buttonSize="xs"
                  buttonStyle="success-new"
                  on:click={handlePairUnpairClick}>

            <IconedContent icon="arrow-left">
                Spárovat
            </IconedContent>
        </KpButton>
    </td>
{/if}

<td>
    <div class="name-container">
        {#if exists(workerField)}
            <GridFieldValue field={workerField}>
                <KpUserAvatar userRecordId={workerField?.originRecordReference?.id} sizePx={24}/>
                {getTextFromGridFieldValue(workerField)}
            </GridFieldValue>
        {/if}

        {#if exists(machineField)}
            <GridFieldValue field={machineField}>
                <IconedContent icon="gears">
                    {getTextFromGridFieldValue(machineField)}
                </IconedContent>
            </GridFieldValue>
        {/if}

        {#if exists(nameField) && FOND_REPORT.thingReports.includes(record.fond.id)}
            <GridFieldValue field={nameField}>
                <IconedContent icon="cube">
                    {getTextFromGridFieldValue(nameField)}
                </IconedContent>
            </GridFieldValue>
        {/if}
    </div>
</td>

<td>
    <GridFieldValue field={dateField}/>
</td>

<td>
    <div class="reported-and-charged-value-container" use:tooltip={{enabled: true, content: reportedValueStrings.tooltipLabel, role: 'tooltip'}}>
        {reportedValueStrings.value}
    </div>
</td>

<td>
    <div class="reported-and-charged-value-container" use:tooltip={{enabled: true, content: chargedValueStrings.tooltipLabel, role: 'tooltip'}}>
        {chargedValueStrings.value}
    </div>
</td>

<td>
    {pipe(record.fond, loc())}
</td>

{#if pairingType === 'pair'}
    <td>
        <KpChipTag chipSize="xs"
                   chipStyle={FOND_REPORT.STATE_CHIP_STYLES[getIdFromGridFieldValue(stateField)] ?? 'default'}>

            {getTextFromGridFieldValue(stateField)}
        </KpChipTag>
    </td>
{/if}

{#if pairingType === 'unpair'}
    <td>
        <KpButton isLoading={isProcessing} noWrap buttonSize="xs" buttonStyle="danger-new" on:click={handlePairUnpairClick}>
            <IconedContent trailingIcon="arrow-right">
                Odpárovat
            </IconedContent>
        </KpButton>
    </td>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .name-container {
        display: inline-flex;
        flex-direction: column;
        gap: @spacing-xs;
    }

    .reported-and-charged-value-container {
        align-items: center;
        width: min-content;
        white-space: nowrap;
        gap: @spacing-s;
        min-width: 100px;
        display: flex;
        justify-content: space-between;
    }
</style>